/**
 * Module: screens/onboarding/coachmarks
 * Role: SSOT for the buyer first-home coachmarks tour (FTR-1114).
 *       - COACH_TARGETS: the 3 data-coach anchor names the overlay querySelects.
 *       - CoachmarksState: PreparingStep → HomeScreen navigation-state contract.
 *       - TIPS: declarative tour step config (ported from design onboarding.jsx:435-543).
 *
 * SYMMETRIC LIMIT (ADR-022 §6) — the data-coach attribute VALUES below are mirrored
 * as string literals in the 3 producer files (all mounted on the home screen, where the
 * tour runs). Keep in sync; enforced by tests/architecture/test_coachmarks_dom_contract.py:
 *   - miniapp/src/screens/HomeScreen/HomeHeader.tsx          data-coach="wallet" (копилка pill)
 *   - miniapp/src/screens/HomeScreen/RecommendationsList.tsx data-coach="feed"
 *   - miniapp/src/screens/HomeScreen/HomeHeader.tsx          data-coach="bell"
 *
 * Used by: screens/onboarding/CoachmarksOverlay (querySelector), screens/HomeScreen/index (trigger)
 * Glossary: ai/glossary/buyer.md
 */

export const COACH_TARGETS = {
  wallet: 'wallet',
  feed: 'feed',
  bell: 'bell',
} as const;

export type CoachTarget = (typeof COACH_TARGETS)[keyof typeof COACH_TARGETS];

/**
 * Navigation-state contract: PreparingStep emits `navigate('/', { state: { showCoachmarks: true } })`
 * and HomeScreen reads it via `useLocation().state`.
 */
export interface CoachmarksState {
  showCoachmarks?: boolean;
}

/** sessionStorage guard key — fires the tour exactly once per session. */
export const COACHMARKS_SEEN_KEY = 'awardy:onboarding:coachmarks-seen';

export interface TipConfig {
  /** data-coach anchor name to highlight (must be a COACH_TARGETS value). */
  target: CoachTarget;
  /** cutout corner radius in px. */
  radius: number;
  title: string;
  body: string;
  /** handwritten accent sticker over the tooltip. */
  caveat: string;
  caveatRot: number;
  /** preferred tooltip placement; overlay auto-flips when there is no room. */
  tipBelow: boolean;
}

export const TIPS: TipConfig[] = [
  {
    target: COACH_TARGETS.wallet,
    radius: 18,
    title: 'Это твоя копилка',
    body: 'Сюда падает кешбэк за каждое принятое задание. Когда соберётся 1 000 ₽ — выведешь на карту.',
    caveat: 'тут деньги',
    caveatRot: -6,
    tipBelow: true,
  },
  {
    target: COACH_TARGETS.feed,
    radius: 18,
    title: 'Здесь — находки',
    body: 'Список товаров на сегодня. Тапни карточку — увидишь, сколько вернётся и условия.',
    caveat: 'выбирай!',
    caveatRot: 4,
    tipBelow: false,
  },
  {
    target: COACH_TARGETS.bell,
    radius: 20,
    title: 'Бот пишет первый',
    body: 'Колокольчик в углу и бот синхронны. Один статус на двух экранах.',
    caveat: 'жми сюда',
    caveatRot: 6,
    tipBelow: true,
  },
];
